import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, LinearProgress, Stack,
  Chip, CircularProgress, Grid
} from '@mui/material';
import {
  Target, Zap, Map, CheckCircle, ArrowRight, ArrowLeft,
  Sparkles, Clock, TrendingUp, BookOpen, AlertCircle, Play
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app';
const BG = '#09090b';
const CARD_SX = { background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' };
const T_PRIMARY = '#fafafa';
const T_SECONDARY = '#a1a1aa';
const T_MUTED = '#71717a';

const GOAL_EXAMPLES = [
  'Become a full-stack web developer in 6 months',
  'Learn machine learning and AI fundamentals',
  'Master renewable energy engineering',
  'Build mobile apps with React Native',
  'Become a data scientist working with Python',
  'Learn cloud architecture on AWS',
];

const GENERATING_MESSAGES = [
  { msg: 'Analyzing your goal and experience level...', pct: 15 },
  { msg: 'Identifying the optimal skill sequence...', pct: 35 },
  { msg: 'Calculating time estimates and milestones...', pct: 55 },
  { msg: 'Personalizing your learning path...', pct: 75 },
  { msg: 'Finalizing your personalized roadmap...', pct: 95 },
];

const DIFF_COLORS = { beginner: '#10b981', intermediate: '#f59e0b', advanced: '#ef4444', expert: '#8b5cf6' };

const StepDot = ({ step, activeStep, label, icon: Icon }) => {
  const done = step < activeStep;
  const active = step === activeStep;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: done ? '#10b981' : active ? '#6366f1' : 'rgba(255,255,255,0.06)',
        border: active ? '2px solid #a5b4fc' : 'none', transition: 'all .3s'
      }}>
        {done ? <CheckCircle size={18} color="#fff" /> : <Icon size={16} color={active ? '#fff' : T_MUTED} />}
      </Box>
      <Typography variant="caption" color={active ? T_PRIMARY : T_MUTED} fontWeight={active ? 600 : 400}
        sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'center' }}>
        {label}
      </Typography>
    </Box>
  );
};

const GoalStep = ({ goalText, setGoalText, onNext, error, loading }) => (
  <Box>
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(99,102,241,0.15)',
        border: '1px solid rgba(99,102,241,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
        <Target size={26} color="#a5b4fc" />
      </Box>
      <Typography variant="h5" fontWeight={800} color={T_PRIMARY} mb={0.5}>What do you want to achieve?</Typography>
      <Typography variant="body2" color={T_MUTED}>Describe your learning goal in your own words.</Typography>
    </Box>
    <TextField multiline rows={4} fullWidth value={goalText} onChange={e => setGoalText(e.target.value)}
      placeholder="e.g. I want to become a full-stack developer and build web applications..."
      sx={{ mb: 2,
        "& .MuiInputBase-root": { bgcolor: 'rgba(255,255,255,0.03)', color: T_PRIMARY, borderRadius: 2 },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: 'rgba(255,255,255,0.1)' },
        "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: 'rgba(255,255,255,0.2)' },
        "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: '#6366f1' },
        "& .MuiInputBase-input::placeholder": { color: T_MUTED }
      }} />
    {error && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: '#f87171' }}>
        <AlertCircle size={16} /><Typography variant="body2" color="#f87171">{error}</Typography>
      </Box>
    )}
    <Typography variant="caption" color={T_MUTED} mb={1.5} display="block">Try an example:</Typography>
    <Stack direction="row" gap={1} flexWrap="wrap" mb={3}>
      {GOAL_EXAMPLES.map(ex => (
        <Chip key={ex} label={ex} size="small" onClick={() => setGoalText(ex)}
          sx={{ bgcolor: 'rgba(99,102,241,0.1)', color: '#a5b4fc', border: '1px solid rgba(99,102,241,0.2)',
            cursor: 'pointer', fontSize: 11, "&:hover": { bgcolor: 'rgba(99,102,241,0.18)' } }} />
      ))}
    </Stack>
    <Button variant="contained" fullWidth size="large" disabled={!goalText.trim() || loading}
      onClick={onNext} endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ArrowRight size={18} />}
      sx={{ bgcolor: '#6366f1', "&:hover": { bgcolor: '#4f46e5' }, borderRadius: 2, py: 1.5,
        "&:disabled": { bgcolor: 'rgba(99,102,241,0.3)', color: 'rgba(255,255,255,0.4)' } }}>
      {loading ? 'Analyzing...' : 'Analyze & Continue'}
    </Button>
  </Box>
);

const GeneratingStep = ({ progress, message }) => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
      <CircularProgress size={80} thickness={2} variant="determinate" value={progress} sx={{ color: '#6366f1' }} />
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Sparkles size={28} color="#a5b4fc" />
      </Box>
    </Box>
    <Typography variant="h6" fontWeight={700} color={T_PRIMARY} mb={1}>Building your learning path</Typography>
    <Typography variant="body2" color="#a5b4fc" mb={3}>{message}</Typography>
    <Box sx={{ maxWidth: 400, mx: 'auto' }}>
      <LinearProgress variant="determinate" value={progress}
        sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)',
          "& .MuiLinearProgress-bar": { bgcolor: '#6366f1', borderRadius: 3 } }} />
      <Typography variant="caption" color={T_MUTED} mt={1} display="block">{progress}% complete</Typography>
    </Box>
  </Box>
);

const PathPreviewStep = ({ generatedPaths, selectedPath, setSelectedPath, goalText }) => {
  if (!generatedPaths) return null;
  const paths = [generatedPaths.mainPath, ...(generatedPaths.alternatives || [])].filter(Boolean);
  const LABELS = ['Recommended Path', 'Accelerated Path', 'Comprehensive Path'];
  const EMOJIS = ['target', 'zap', 'book'];
  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={800} color={T_PRIMARY} mb={0.5}>Your Learning Paths</Typography>
        <Typography variant="body2" color={T_MUTED}>
          AI generated {paths.length} option{paths.length !== 1 ? 's' : ''} for: <em style={{ color: '#a5b4fc' }}>{goalText}</em>
        </Typography>
      </Box>
      <Stack spacing={2} mb={3}>
        {paths.slice(0, 3).map((path, i) => {
          const isSelected = selectedPath === path || (!selectedPath && i === 0);
          return (
            <Box key={i} onClick={() => setSelectedPath(path)}
              sx={{ ...CARD_SX, p: 2.5, cursor: 'pointer',
                borderColor: isSelected ? '#6366f1' : 'rgba(255,255,255,0.08)',
                background: isSelected ? 'rgba(99,102,241,0.06)' : CARD_SX.background, transition: 'all .2s' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isSelected && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1' }} />}
                  <Typography variant="subtitle2" fontWeight={700} color={T_PRIMARY}>{LABELS[i] || 'Path ' + (i+1)}</Typography>
                </Box>
                {isSelected && <Chip label="Selected" size="small" sx={{ bgcolor: '#6366f133', color: '#a5b4fc', fontSize: 11 }} />}
              </Box>
              <Stack direction="row" gap={2} mb={1.5} flexWrap="wrap">
                <Typography variant="caption" color={T_MUTED}>{path.estimatedMonths || 6} months</Typography>
                <Typography variant="caption" color={T_MUTED}>{path.hoursPerWeek || 12}h/week</Typography>
                <Typography variant="caption" color={T_MUTED}>{(path.skills || []).length} skills</Typography>
                <Typography variant="caption" color="#10b981">
                  {Math.round((generatedPaths.summary?.successRate || path.successProbability * 100 || 75))}% success rate
                </Typography>
              </Stack>
              {path.skills && (
                <Stack direction="row" gap={0.5} flexWrap="wrap">
                  {path.skills.slice(0, 5).map((skill, j) => (
                    <Chip key={j} label={skill.name || skill} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: T_SECONDARY, fontSize: 10, height: 20 }} />
                  ))}
                  {path.skills.length > 5 && (
                    <Chip label={"+" + (path.skills.length - 5)} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.04)', color: T_MUTED, fontSize: 10, height: 20 }} />
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {(selectedPath || paths[0]) && ((selectedPath || paths[0]).milestones || []).length > 0 && (
        <Box sx={{ ...CARD_SX, p: 2.5, bgcolor: 'rgba(16,185,129,0.04)', borderColor: 'rgba(16,185,129,0.15)' }}>
          <Typography variant="subtitle2" fontWeight={700} color="#10b981" mb={1.5}>Milestones</Typography>
          <Stack spacing={1}>
            {((selectedPath || paths[0]).milestones || []).slice(0, 4).map((m, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#10b98122',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                  <Typography variant="caption" color="#10b981" fontWeight={700}>{i + 1}</Typography>
                </Box>
                <Typography variant="caption" color={T_SECONDARY} lineHeight={1.6}>{m}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const ConfirmStep = ({ selectedPath, generatedPaths, goalText, onLaunch, loading, error }) => {
  const path = selectedPath || generatedPaths?.mainPath;
  if (!path) return null;
  const summaryItems = [
    { label: 'Goal', value: goalText ? goalText.substring(0, 60) + (goalText.length > 60 ? '...' : '') : '' },
    { label: 'Duration', value: (path.estimatedMonths || 6) + ' months' },
    { label: 'Weekly Commitment', value: (path.hoursPerWeek || 12) + ' hours/week' },
    { label: 'Total Skills', value: (path.skills?.length || 0) + ' skills to master' },
  ];
  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(16,185,129,0.15)',
          border: '1px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <CheckCircle size={26} color="#10b981" />
        </Box>
        <Typography variant="h5" fontWeight={800} color={T_PRIMARY} mb={0.5}>Ready to launch?</Typography>
        <Typography variant="body2" color={T_MUTED}>Your path will be activated and tracking will begin.</Typography>
      </Box>
      <Box sx={{ ...CARD_SX, p: 3, mb: 3, background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(99,102,241,0.04))' }}>
        <Typography variant="subtitle2" fontWeight={700} color="#a5b4fc" mb={2}>Path Summary</Typography>
        <Grid container spacing={2}>
          {summaryItems.map(item => (
            <Grid item xs={12} sm={6} key={item.label}>
              <Typography variant="caption" color={T_MUTED}>{item.label}</Typography>
              <Typography variant="body2" fontWeight={600} color={T_PRIMARY}>{item.value}</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
      {error && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, p: 2, bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, border: '1px solid rgba(239,68,68,0.2)' }}>
          <AlertCircle size={16} color="#f87171" style={{ flexShrink: 0, marginTop: 2 }} />
          <Typography variant="body2" color="#f87171">{error}</Typography>
        </Box>
      )}
      <Button variant="contained" fullWidth size="large" onClick={onLaunch} disabled={loading}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Play size={18} />}
        sx={{ bgcolor: '#10b981', "&:hover": { bgcolor: '#059669' }, borderRadius: 2, py: 1.5, mb: 1.5,
          "&:disabled": { bgcolor: 'rgba(16,185,129,0.3)' } }}>
        {loading ? 'Activating your path...' : 'Launch My Learning Path'}
      </Button>
      <Typography variant="caption" color={T_MUTED} display="block" textAlign="center">
        You can adjust or pause your path at any time from your dashboard.
      </Typography>
    </Box>
  );
};

const CreatePathWizardNew = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [goalText, setGoalText] = useState('');
  const [generatedPaths, setGeneratedPaths] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const [genMessage, setGenMessage] = useState(GENERATING_MESSAGES[0].msg);
  const [formData, setFormData] = useState({
    goalText: '', parsedGoal: null, timeline: 6, intensity: 'moderate', hoursPerWeek: 12,
    currentSkills: [], targetSkills: [], learningStyle: 'balanced', preferredFormat: 'mixed'
  });

  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (!token) {
      setError('Please log in to create your personalized learning path');
      setTimeout(() => navigate('/login', { state: { returnTo: '/create-path' } }), 2000);
    }
  }, [navigate]);

  const intelligentlyParseGoal = useCallback((text) => {
    const lower = text.toLowerCase();
    const detectCategory = (t) => {
      const cats = {
        language: ['language','speak','fluent','french','spanish','english','chinese','japanese','korean','german','arabic','lingala'],
        cooking: ['cook','bake','recipe','cuisine','chef','food','kitchen'],
        music: ['music','guitar','piano','violin','sing','instrument','chord'],
        tech: ['code','programming','developer','software','web','app','javascript','python','react','ai','machine learning','data'],
        business: ['business','entrepreneur','startup','marketing','finance','management'],
        fitness: ['fitness','gym','workout','yoga','exercise','health'],
      };
      for (const [cat, kws] of Object.entries(cats)) {
        if (kws.some(k => t.includes(k))) return cat;
      }
      return 'professional';
    };
    const category = detectCategory(lower);
    return {
      targetRole: text.charAt(0).toUpperCase() + text.slice(1),
      targetIndustry: category,
      currentLevel: 'beginner',
      category,
      specificInterests: text.split(/\s+/).filter(w => w.length > 4).slice(0, 4).map(w => w.charAt(0).toUpperCase() + w.slice(1)),
      originalText: text
    };
  }, []);

  const analyzeGoal = useCallback(async () => {
    if (!goalText.trim()) return;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    let parsed = null;
    try {
      const res = await fetch(API_BASE + '/api/ai/parse-goal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ goalText: goalText.trim() })
      });
      if (res.ok) { const d = await res.json(); parsed = d.data; }
    } catch (_) {}
    if (!parsed) {
      try {
        const chatRes = await fetch(API_BASE + '/api/ai/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({
            messages: [{ role: 'user', content: 'Goal: "' + goalText + '". Reply with ONLY JSON: {"targetRole":"...","targetIndustry":"...","currentLevel":"beginner","category":"tech","specificInterests":["skill1","skill2"]}' }],
            context: 'goal_parsing'
          })
        });
        if (chatRes.ok) {
          const chatData = await chatRes.json();
          let content = (chatData.data?.content || chatData.content || '').trim();
          if (content.startsWith('```')) content = content.replace(/^```json?\s*/, '').replace(/\s*```$/, '');
          try { parsed = JSON.parse(content); } catch (_) {}
        }
      } catch (_) {}
    }
    if (!parsed) parsed = intelligentlyParseGoal(goalText);
    setFormData(prev => ({ ...prev, goalText, parsedGoal: parsed }));
    setLoading(false);
    await doGeneratePaths(parsed);
  }, [goalText, intelligentlyParseGoal]);

  const doGeneratePaths = useCallback(async (parsed) => {
    setStep(1);
    setLoading(true);
    setGenProgress(0);
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    let msgIdx = 0;
    const timer = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, GENERATING_MESSAGES.length - 1);
      setGenProgress(GENERATING_MESSAGES[msgIdx].pct);
      setGenMessage(GENERATING_MESSAGES[msgIdx].msg);
    }, 1500);
    try {
      const res = await fetch(API_BASE + '/api/learning-paths/ai/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          goalText: goalText,
          preferences: { timeline: formData.timeline, intensity: formData.intensity, maxHoursPerWeek: formData.hoursPerWeek,
            currentSkills: formData.currentSkills, learningStyle: formData.learningStyle, preferredFormat: formData.preferredFormat },
          saveToProfile: false
        })
      });
      clearInterval(timer);
      if (!res.ok) throw new Error('Failed to generate paths');
      const data = await res.json();
      setGeneratedPaths(data.data);
      setSelectedPath(data.data.alternatives?.[1] || data.data.mainPath);
      setGenProgress(100);
      await new Promise(r => setTimeout(r, 600));
      setStep(2);
    } catch (err) {
      clearInterval(timer);
      setError(err.message || 'Failed to generate paths. Please try again.');
      setStep(0);
    } finally {
      setLoading(false);
    }
  }, [goalText, formData]);

  const launchPath = useCallback(async () => {
    setLoading(true);
    setError('');
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    const path = selectedPath || generatedPaths?.mainPath;
    try {
      const res = await fetch(API_BASE + '/api/learning-paths', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          targetRole: generatedPaths?.summary?.targetRole || formData.parsedGoal?.targetRole,
          skills: path.skills,
          milestones: path.milestones || [],
          estimatedCompletion: { months: path.estimatedMonths || 6, hours: path.totalHours || 0 },
          hoursPerWeek: path.hoursPerWeek || formData.hoursPerWeek,
          successProbability: (generatedPaths?.summary?.successRate || 75) / 100,
          isActive: true,
          preferences: { preferredFormat: formData.preferredFormat, learningStyle: formData.learningStyle }
        })
      });
      if (!res.ok) throw new Error('Failed to activate path');
      toast.success('Your learning path is ready!', { duration: 3000, icon: 'rocket' });
      localStorage.setItem('portalShouldRefresh', 'true');
      setTimeout(() => navigate('/studentportal'), 1800);
    } catch (err) {
      setError(err.message || 'Failed to activate path. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPath, generatedPaths, formData, navigate]);

  const STEPS = [
    { label: 'Your Goal', icon: Target },
    { label: 'Generating', icon: Sparkles },
    { label: 'Preview', icon: Map },
    { label: 'Launch', icon: CheckCircle },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: BG, color: T_PRIMARY,
      display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 4 } }}>
      <Box sx={{ width: '100%', maxWidth: 600 }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h4" fontWeight={900} color={T_PRIMARY} mb={0.5}>
            <Box component="span" sx={{ background: 'linear-gradient(135deg,#6366f1,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              AI Learning Path
            </Box>
          </Typography>
          <Typography variant="body2" color={T_MUTED}>Personalized by AI. Built for you.</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, px: 2 }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.label}>
              <StepDot step={i} activeStep={step} label={s.label} icon={s.icon} />
              {i < STEPS.length - 1 && (
                <Box sx={{ flex: 1, height: 2, bgcolor: step > i ? '#6366f133' : 'rgba(255,255,255,0.06)', mx: 0.5, borderRadius: 1, transition: 'all .3s' }} />
              )}
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ ...CARD_SX, p: { xs: 3, md: 4 } }}>
          {step === 0 && <GoalStep goalText={goalText} setGoalText={setGoalText} onNext={analyzeGoal} error={error} loading={loading} />}
          {step === 1 && <GeneratingStep progress={genProgress} message={genMessage} />}
          {step === 2 && (
            <>
              <PathPreviewStep generatedPaths={generatedPaths} selectedPath={selectedPath}
                setSelectedPath={setSelectedPath} goalText={goalText} />
              <Stack direction="row" gap={2} mt={3}>
                <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => setStep(0)}
                  sx={{ borderColor: 'rgba(255,255,255,0.1)', color: T_SECONDARY, borderRadius: 2, "&:hover": { borderColor: 'rgba(255,255,255,0.2)' } }}>
                  Change Goal
                </Button>
                <Button variant="contained" endIcon={<ArrowRight size={16} />} onClick={() => setStep(3)}
                  sx={{ flex: 1, bgcolor: '#6366f1', "&:hover": { bgcolor: '#4f46e5' }, borderRadius: 2 }}>
                  Looks Good!
                </Button>
              </Stack>
            </>
          )}
          {step === 3 && (
            <>
              <ConfirmStep selectedPath={selectedPath} generatedPaths={generatedPaths}
                goalText={goalText} onLaunch={launchPath} loading={loading} error={error} />
              <Button variant="text" startIcon={<ArrowLeft size={16} />} onClick={() => setStep(2)}
                sx={{ mt: 1, color: T_MUTED, width: '100%' }}>
                Back to Preview
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CreatePathWizardNew;
